// @ts-nocheck
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import { addWishListLines, deleteWishListLines } from "@insite/client-framework/Services/WishListService";
import { updateWishListLine as updateWishListLineApi } from "@insite/client-framework/Services/WishListService";
import loadWishListLines from "@insite/client-framework/Store/Pages/MyListDetails/Handlers/LoadWishListLines";
import { WishListLineModel, WishListModel } from "@insite/client-framework/Types/ApiModels";
import useHandleApiError from "@store/Framework/Common/Hooks/useHandleApiError";
import {
    BuyingList,
    getAllBuyingListsAsWishLists,
    getBuyingListById,
    updateBuyingList,
} from "@store/Framework/Services/RestrictedBuyingListService";
import useBuyingListState from "@store/Framework/Store/Bunzl/Pages/Reporting/StoreHooks/useBuyingListState";
import { toToolkitApplicationState } from "@toolkit/Framework/Store/ApplicationState";
import { useState } from "react";
import { useDispatch } from "react-redux";

interface DeleteOptions {
    itemsToRemove: string[];
}
interface AddOptions {
    itemsToAdd: string[];
}
type UpdateBuyingListOptions = DeleteOptions | AddOptions | undefined;
const useBuyingList = () => {
    const isLoading = useAppSelector(state => toToolkitApplicationState(state).bunzl.pages.buyingList.isLoading);
    const activeBuyingList = useAppSelector(
        state => toToolkitApplicationState(state).bunzl.pages.buyingList.activeBuyingList,
    );
    const activeWishList = useAppSelector(
        state => toToolkitApplicationState(state).bunzl.pages.buyingList.activeWishList,
    );
    const {
        buyingLists,
        setBuyingLists,
        setBuyingListsIsLoading: setLoading,
        setApiParams: setPaginationParams,
    } = useBuyingListState();
    const apiParams = useAppSelector(state => toToolkitApplicationState(state).bunzl.pages.buyingList.apiParams);
    const [assignedBuyingList, setAssignedBuyingList] = useState<BuyingList>();
    const [error, setError] = useState<unknown>();
    const dispatch = useDispatch();
    const refreshActiveBuyingList = (newBuyingList: BuyingList) => {
        if (!activeBuyingList) {
            return;
        }
        dispatch({ type: "Stores/Pages/buyingList/LoadActiveBuyingList", activeBuyingList: newBuyingList });
    };
    const handleBuyingListApiError = useHandleApiError();

    const setPage = (page: number) => {
        if (page === apiParams.page) {
            return;
        }
        setPaginationParams({
            ...apiParams,
            page,
        });
    };
    const setPageSize = (pageSize: number) => {
        if (pageSize === apiParams.pageSize) {
            return;
        }
        setPaginationParams({
            ...apiParams,
            pageSize,
        });
    };
    const updateBuyingListLine = async (wishListLine: WishListLineModel) => {
        if (!activeWishList || !activeBuyingList) {
            return;
        }
        if (!isLoading) {
            setLoading(true);
        }
        await handleBuyingListApiError(async () => {
            await updateWishListLineApi({
                wishListId: activeBuyingList.wishListId,
                wishListLineId: wishListLine.id,
                wishListLine,
            });
            refreshWishListLineState();
            getBuyingLists(true);
        });
        setLoading(false);
    };

    const setBuyingListQuery = (query: string) => {
        // this shouldn't work but for some reason it does - apiParams occaionally comes back undefined for no reason
        if (query === apiParams.query) {
            return;
        }
        setPaginationParams({ ...apiParams, query, page: 1 });
    };
    const updateAssignedCustomers = async (buyingListId: string, customerIds: string[]) => {
        await handleBuyingListApiError(async () =>
            refreshActiveBuyingList(await updateBuyingList(buyingListId, { properties: {}, uri: "", customerIds })),
        );
    };

    const getAssignedBuyingList = async (buyingListId: string) => {
        if (!isLoading) {
            setLoading(true);
        }
        const assignedBuyingList = await handleBuyingListApiError(() => getBuyingListById(buyingListId));
        setAssignedBuyingList(assignedBuyingList);
        setLoading(false);
        return assignedBuyingList;
    };
    const updateAssignedUsers = async (buyingListId: string, userIds: string[]) => {
        const assignedReturn = await handleBuyingListApiError(() =>
            updateBuyingList(buyingListId, { properties: {}, uri: "", userIds }),
        );
        setAssignedBuyingList(assignedReturn);
    };
    const toggleActiveBuyingListOverride = async () => {
        if (!activeBuyingList) {
            return;
        }

        const newBuyingList = await handleBuyingListApiError(() =>
            updateBuyingList(activeBuyingList.id, {
                properties: {},
                uri: "",
                overrideBillToLists: !activeBuyingList?.overrideBillToLists,
            }),
        );
        newBuyingList && (await refreshActiveBuyingList(newBuyingList));
    };
    const refreshWishListLineState = () => {
        dispatch({ type: "Data/WishListLines/Reset" });
        dispatch({ type: "Data/WishLists/Reset" });
        dispatch(loadWishListLines());
    };
    const updateBuyingLists = async (buyingList: WishListModel, options: UpdateBuyingListOptions = undefined) => {
        setLoading(true);

        if (options && "itemsToRemove" in options) {
            await handleBuyingListApiError(() =>
                deleteWishListLines({ wishListId: buyingList.id, wishListLineIds: options.itemsToRemove }),
            );
        }
        if (options && "itemsToAdd" in options) {
            const { itemsToAdd } = options;
            const newProductIDs = itemsToAdd.filter(
                productId => !buyingList?.wishListLineCollection?.some(line => line.productId === productId),
            );
            await handleBuyingListApiError(() =>
                addWishListLines({
                    wishList: buyingList,
                    lines: newProductIDs.map(productId => ({ productId, qtyOrdered: 1, unitOfMeasure: "EA" })),
                }),
            );
        }

        if (activeBuyingList) {
            await handleBuyingListApiError(() => getBuyingLists(true));
            refreshWishListLineState();
        }

        setLoading(false);
    };

    const getBuyingLists = async (forceUpdate = false): Promise<void> => {
        if ((buyingLists || isLoading) && !forceUpdate) {
            return;
        }
        setLoading(true);
        const response = await handleBuyingListApiError(() => getAllBuyingListsAsWishLists(apiParams));
        setBuyingLists(response);
        setLoading(false);
    };
    return {
        getBuyingLists,
        setLoading,
        loading: isLoading,
        error,
        updateBuyingListLine,
        buyingLists,
        updateBuyingLists,
        assignedBuyingList,
        setBuyingListQuery,
        updateAssignedUsers,
        getAssignedBuyingList,
        updateAssignedCustomers,
        activeBuyingList,
        setPage,
        activeWishList,
        toggleActiveBuyingListOverride,
        setPageSize,
    };
};

export default useBuyingList;
